import React, { useState, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Slider,
  Typography,
} from "@material-ui/core";
import Cropper from "react-easy-crop";

const OUTPUT_SIZE = 512; // 512x512 px
const OUTPUT_QUALITY = 0.85;

/**
 * Recorta un área del canvas y devuelve un Blob JPEG cuadrado de OUTPUT_SIZE x OUTPUT_SIZE.
 */
const cropToBlob = (imageSrc, cropArea) => {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = OUTPUT_SIZE;
      canvas.height = OUTPUT_SIZE;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(
        image,
        cropArea.x,
        cropArea.y,
        cropArea.width,
        cropArea.height,
        0,
        0,
        OUTPUT_SIZE,
        OUTPUT_SIZE,
      );
      canvas.toBlob(
        (blob) => {
          if (blob) resolve(blob);
          else reject(new Error("No se pudo generar el blob."));
        },
        "image/jpeg",
        OUTPUT_QUALITY,
      );
    };
    image.onerror = () => reject(new Error("No se pudo cargar la imagen."));
    image.src = imageSrc;
  });
};

/**
 * Dialog reusable para recortar una imagen cuadrada con zoom + drag.
 * Output: Blob JPEG 512x512 quality 0.85.
 *
 * Props:
 *   - open: boolean
 *   - onClose: () => void
 *   - imageSrc: string (URL data: o blob:)
 *   - onCrop: (blob: Blob) => void
 */
const ImageCropDialog = ({ open, onClose, imageSrc, onCrop }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [procesando, setProcesando] = useState(false);

  useEffect(() => {
    if (open) {
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
    }
  }, [open]);

  const onCropComplete = useCallback((_croppedArea, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const aplicar = useCallback(async () => {
    if (!croppedAreaPixels) return;
    setProcesando(true);
    try {
      const blob = await cropToBlob(imageSrc, croppedAreaPixels);
      onCrop(blob);
      onClose();
    } catch (err) {
      // El consumer maneja el error vía snackbar
      console.error(err);
    } finally {
      setProcesando(false);
    }
  }, [croppedAreaPixels, imageSrc, onCrop, onClose]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Recortar foto</DialogTitle>
      <DialogContent>
        <Box
          sx={{
            position: "relative",
            width: "100%",
            height: 320,
            mb: 3,
            bgcolor: "#000",
          }}
        >
          {imageSrc && (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={1}
              cropShape="round"
              showGrid={false}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          )}
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Zoom
          </Typography>
          <Slider
            value={zoom}
            min={1}
            max={3}
            step={0.05}
            onChange={(_, value) => setZoom(value)}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button
          onClick={aplicar}
          variant="contained"
          disabled={!croppedAreaPixels || procesando}
        >
          {procesando ? "Procesando..." : "Aplicar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ImageCropDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  imageSrc: PropTypes.string,
  onCrop: PropTypes.func.isRequired,
};

export default ImageCropDialog;
